/**
 * Bustle is a highly performant event bus that offers event order guarantees.
 */

import Zousan from 'zousan';

import type { BusPayload, EventName, MessageBus, Unsubscribe } from '@typings/bus.ts';

/**
 * Every bus payload is a plain object literal: string-named properties whose
 * values may be anything. Events that carry no payload pass `undefined`.
 * Declare event payload shapes with `type` aliases, not `interface` — interfaces
 * lack the implicit index signature needed to satisfy this constraint.
 */
type RuntimeBusPayload = BusPayload | undefined;
type RuntimeBusResult = unknown;
type RuntimeBusListener = (payload: RuntimeBusPayload) => RuntimeBusResult;
type RuntimeBusMonitor = (payload: RuntimeBusPayload, results: Promise<RuntimeBusResult[]>) => void;

export type { BusPayload, MessageBus, Unsubscribe };

interface BustleResponse {
  responseOrder?: number | null;
}

interface BustleLog {
  info: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  sublog?: (name: string, options?: Record<string, unknown>) => BustleLog;
}

interface CreateOptions {
  log?: BustleLog;
  rejectOnError?: boolean;
  reportAllErrors?: boolean;
  showEvents?: boolean | ((eventName: EventName, payload: RuntimeBusPayload) => boolean);
}

/**
 * Escape hatch for the few legacy events whose payload predates the
 * {@link BusPayload} object-literal rule and carries a bare string, boolean,
 * or array (the `layers/*` id events, `map/setEnablePoiSelection`). Every use
 * marks a migration candidate — do not use it for new events.
 */
export const legacyBusPayload = (payload: string | boolean | readonly unknown[]): BusPayload =>
  payload as unknown as BusPayload;

const bustleResponseComparitor = (r1: BustleResponse, r2: BustleResponse) => {
  if (r1.responseOrder == null) {
    // null or undefined
    return r2.responseOrder == null ? 0 : 1;
  } // any order is higher pri than none
  if (r2.responseOrder == null) {
    return -1;
  }
  return r1.responseOrder - r2.responseOrder;
};

// Remove element from array at index specified and return the removed element.
// Source array will then have item removed.
const arRmAt = <T>(ar: T[], index: number): T | undefined => ar.splice(index, 1)[0]; // oh yah, thats intuitive!

// Removes the value from the array if it exists. The array is then returned
const arRm = <T>(ar: T[], value: T): T[] => {
  let i = 0;
  do {
    i = ar.indexOf(value, i);
    if (i >= 0) {
      arRmAt(ar, i);
    }
  } while (i >= 0);
  return ar;
};

// This creates a bus instance. When used as a Singleton,  you will only call this once
// for the life of your app. To isolate events (perhaps for performance or security
// reasons) you can create multple bus instances.
export function create(opts: CreateOptions = {}): MessageBus {
  // If a log option is defined, use it - and if it supports sublogs, instantiate that. If all else fails, use console
  const log = opts.log ? (opts.log.sublog ? opts.log.sublog('bustle', { color: 'pink' }) : opts.log) : console;

  const ons: Record<EventName, RuntimeBusListener[]> = {}; // holds your listeners
  const mons: Record<EventName, RuntimeBusMonitor[]> = {}; // holds your monitors

  // subscribes to an event. Your listener will always be called
  // with a single argument. I am considering adding another way
  // to subscribe to events by observing an event and then
  // subscribing to that observer.
  function on(ev: EventName, fn: RuntimeBusListener) {
    if (!ons[ev]) {
      ons[ev] = [];
    }

    ons[ev].push(fn);

    return () => off(ev, fn);
  }

  // Similar to "on" but is always called after all normal "on" listeners,
  // and any values returned by a monitor call are ignored (not added to the
  // response). Also, any calls to monitor listeners contains both the message
  // object AND a promise containing the response from the normal listeners.
  function monitor(ev: EventName, fn: RuntimeBusMonitor) {
    if (!mons[ev]) {
      mons[ev] = [];
    }

    mons[ev].push(fn);

    return () => moff(ev, fn);
  }

  function off(ev: EventName, fn: RuntimeBusListener) {
    // run ASAP but not immediate, in case it removes in its own function
    Zousan.soon(() => {
      if (!ons[ev]) {
        return;
      }
      arRm(ons[ev], fn);
    });
  }

  function moff(ev: EventName, fn: RuntimeBusMonitor) {
    // run ASAP but not immediate, in case it removes in its own function
    Zousan.soon(() => {
      if (!mons[ev]) {
        return;
      }
      arRm(mons[ev], fn);
    });
  }

  const serve = (ev: EventName, ob: RuntimeBusPayload, done: (results: Promise<RuntimeBusResult[]>) => void) => () => {
    // const myOns = (ons[ev] || []).concat(ons["*"] ? ons["*"] : [])
    const myOns = ons[ev];
    const myMons = mons[ev];
    const res: RuntimeBusResult[] = [];
    if (myOns) {
      for (const listener of myOns) {
        try {
          res.push(listener(ob));
        } catch (err) {
          if (opts.reportAllErrors) {
            log.error(err);
          }
          if (opts.rejectOnError) {
            res.push(Zousan.reject(err));
          } // will cause the Zousan.all to reject the send
          else {
            res.push(err);
          }
        }
      }
    }

    const results: Promise<RuntimeBusResult[]> = Zousan.all(res);

    if (myMons) {
      for (const listener of myMons) {
        try {
          listener(ob, results);
        } catch (err) {
          if (opts.reportAllErrors) {
            log.error(err);
          }
        }
      }
    }

    done(results);
  };

  // Used when there is a single listener that provides information or a service,
  // a get sends a message to that listener, ensures it gets exactly 1 result,
  // and returns that result. If there are 0 or more than 1 listener, the returned
  // promise is rejected
  const get = (ev: EventName, ob: RuntimeBusPayload) =>
    send(ev, ob).then(res =>
      res.length !== 1
        ? Zousan.reject(`${ev} event did not return a single result, but ${res.length} results.`)
        : res[0],
    );

  // Allows for any number of listeners to exist - but extracts the first one
  // and returns it. If there are NO listeners, undefined is returned. The promise
  // is never rejected.
  const getFirst = (ev: EventName, ob: RuntimeBusPayload) =>
    send(ev, ob).then(res =>
      res.length >= 1 ? (res as BustleResponse[]).sort(bustleResponseComparitor)[0] : undefined,
    );

  function send(ev: EventName, ob: RuntimeBusPayload): Promise<RuntimeBusResult[]> {
    if (opts.showEvents) {
      if (typeof opts.showEvents === 'function') {
        if (opts.showEvents(ev, ob)) {
          log.info('send with', ev, ' and ', ob);
        }
      } else {
        log.info('send with', ev, ' and ', ob);
      }
    }
    return new Zousan((resolve: (results: Promise<RuntimeBusResult[]>) => void) => Zousan.soon(serve(ev, ob, resolve)));
  }

  // The methods are declared generically on MessageBus for caller ergonomics;
  // the implementations operate on `unknown`, so assert the contract here — the
  // one place the bus mints its typed surface.
  return { get, getFirst, moff, monitor, off, on, send } as unknown as MessageBus;
}
